import { useRef, useEffect, useState } from 'react';
import { swapScenarios } from '../data';

const pipValues: Record<string, number> = {
  EURUSD: 10,
  GBPJPY: 7.5,
  USDTRY: 8,
};

const samplePairs = [
  { pair: 'EURUSD', swapLong: -0.48, swapShort: 0.12, pipVal: 10, label: 'Major' },
  { pair: 'GBPJPY', swapLong: 0.82, swapShort: -1.35, pipVal: 7.5, label: 'Cross' },
  { pair: 'USDTRY', swapLong: -28.50, swapShort: 12.40, pipVal: 8, label: 'Exotic' },
];

function fmt(n: number): string {
  const abs = Math.abs(Math.round(n));
  if (abs >= 1000000) return `${n < 0 ? '-' : ''}$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${n < 0 ? '-' : ''}$${Math.round(abs).toLocaleString()}`;
  return `${n < 0 ? '-' : ''}$${abs}`;
}

function fmtPrecise(n: number): string {
  const sign = n < 0 ? '-' : n > 0 ? '+' : '';
  return `${sign}$${Math.abs(Math.round(n)).toLocaleString()}`;
}

interface PairSwapCost {
  pair: string;
  label: string;
  longCost: number;
  shortCost: number;
}

interface ScenarioComputed {
  monthlySpread: number;
  pairCosts: PairSwapCost[];
  dominantSwap: number;
  monthlyTotal: number;
  annualTotal: number;
  swapPct: number;
  spreadPct: number;
}

const scenarioData: ScenarioComputed[] = swapScenarios.map((scenario) => {
  const monthlySpread = scenario.tradesPerMonth * scenario.lotSize * 1.5 * 10;

  const pairCosts: PairSwapCost[] = samplePairs.map((sp) => ({
    pair: sp.pair,
    label: sp.label,
    longCost:
      sp.swapLong * sp.pipVal * scenario.lotSize * scenario.avgHoldDays * scenario.tradesPerMonth,
    shortCost:
      sp.swapShort * sp.pipVal * scenario.lotSize * scenario.avgHoldDays * scenario.tradesPerMonth,
  }));

  const eurusdSwap = pairCosts[0];
  const dominantSwap = Math.max(
    Math.abs(eurusdSwap.longCost),
    Math.abs(eurusdSwap.shortCost),
  );
  const monthlyTotal = monthlySpread + dominantSwap;
  const annualTotal = monthlyTotal * 12;
  const swapPct = monthlyTotal > 0 ? Math.round((dominantSwap / monthlyTotal) * 100) : 0;
  const spreadPct = 100 - swapPct;

  return { monthlySpread, pairCosts, dominantSwap, monthlyTotal, annualTotal, swapPct, spreadPct };
});

const allSwapCosts: number[] = [];
scenarioData.forEach((sd) => {
  sd.pairCosts.forEach((pc) => {
    allSwapCosts.push(Math.abs(pc.longCost));
    allSwapCosts.push(Math.abs(pc.shortCost));
  });
});
const globalMaxSwap = Math.max(...allSwapCosts, 1);

function AnimatedNumber({ value }: { value: number }) {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const start = prevRef.current;
    const end = Math.round(value);
    const duration = 600;
    const startTime = performance.now();

    function easeOutCubic(t: number) {
      return 1 - Math.pow(1 - t, 3);
    }

    function tick(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }

    rafRef.current = requestAnimationFrame(tick);
    prevRef.current = end;
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  return <>{fmt(display)}</>;
}

const scenarioAccents = ['accent', 'secondary', 'primary'] as const;

export default function SwapCalculationSection() {
  return (
    <section className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* ── Section Header ── */}
        <div className="max-w-[720px] mb-10 md:mb-14">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            The longer you hold, the more swap matters
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Three identical market setups — same conviction, same lot sizing discipline, same trade frequency
            relative to style. The only variable: how long you stay in the trade. That single difference reshapes
            your entire cost structure. All totals use EURUSD swap rates as the benchmark.
          </p>
        </div>

        {/* ── COST CASCADE: Three rich scenario panels ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6 mb-10">
          {swapScenarios.map((scenario, idx) => {
            const data = scenarioData[idx];
            const isDay = idx === 0;
            const isSwing = idx === 1;
            const isPosition = idx === 2;
            const acc = scenarioAccents[idx];

            return (
              <div
                key={scenario.label}
                className={`relative bg-white rounded-xl border overflow-hidden transition-all duration-300 ${
                  isPosition
                    ? 'border-primary-200 shadow-[0_0_0_1px_rgba(var(--primary-500),0.06)]'
                    : 'border-background-200'
                }`}
              >
                {/* Top accent strip */}
                <div
                  className={`h-1 w-full ${
                    isDay ? 'bg-accent-500' : isSwing ? 'bg-secondary-500' : 'bg-primary-500'
                  }`}
                />

                <div className="p-5 md:p-6">
                  {/* Header */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-[18px] font-[750] text-foreground-950 tracking-tight">
                        {scenario.label}
                      </h3>
                      {isDay && (
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-accent-50 text-accent-600 border border-accent-200">
                          Swap-free
                        </span>
                      )}
                      {isPosition && (
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-primary-50 text-primary-600 border border-primary-200">
                          High impact
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[13px] text-foreground-500">
                      <span className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-300" />
                        {scenario.tradesPerMonth} trades/mo
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-300" />
                        {scenario.lotSize} {scenario.lotSize === 1 ? 'lot' : 'lots'}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-300" />
                        {scenario.avgHoldDays === 0 ? 'Intraday' : `${scenario.avgHoldDays}d hold`}
                      </span>
                    </div>
                  </div>

                  {/* Monthly cost — BIG */}
                  <div className="mb-5">
                    <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-1">
                      Monthly cost (EURUSD benchmark)
                    </div>
                    <div className="text-[38px] md:text-[44px] font-[800] leading-none tracking-tight text-foreground-950 tabular-nums">
                      <AnimatedNumber value={data.monthlyTotal} />
                    </div>
                    <div className="text-[12px] text-foreground-500 mt-1">
                      Annual: <span className="font-semibold text-foreground-700"><AnimatedNumber value={data.annualTotal} /></span>
                    </div>
                  </div>

                  {/* Cost composition bar */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                        Cost composition
                      </span>
                      {!isDay && (
                        <span className={`text-[11px] font-bold ${data.swapPct >= 50 ? 'text-[#EF4444]' : 'text-foreground-600'}`}>
                          Swap {data.swapPct}% of total
                        </span>
                      )}
                    </div>

                    {isDay ? (
                      <div className="w-full h-8 bg-accent-100/50 rounded-md flex items-center justify-center">
                        <span className="text-[12px] font-semibold text-accent-600">100% Spread — no overnight positions</span>
                      </div>
                    ) : (
                      <>
                        {/* Labels row */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold text-foreground-700">
                            Spread <span className="text-accent-500">{fmt(data.monthlySpread)}</span>
                          </span>
                          <span className={`text-[11px] font-bold text-foreground-700 ${data.swapPct >= 50 ? 'text-[#EF4444]' : ''}`}>
                            Swap <span className={isSwing ? 'text-secondary-500' : 'text-primary-500'}>{fmt(data.dominantSwap)}</span>
                          </span>
                        </div>
                        {/* Pure stacked bar — no text inside */}
                        <div className="w-full h-3 bg-background-100 rounded-full overflow-hidden flex">
                          <div
                            className="h-full bg-accent-500/50 transition-all duration-700"
                            style={{ width: `${data.spreadPct}%` }}
                          />
                          <div
                            className={`h-full transition-all duration-700 ${
                              isSwing ? 'bg-secondary-500/50' : 'bg-primary-500/50'
                            }`}
                            style={{ width: `${data.swapPct}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-sm bg-accent-500/50" />
                            <span className="text-[11px] text-foreground-500">Spread</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2.5 h-2.5 rounded-sm ${isSwing ? 'bg-secondary-500/50' : 'bg-primary-500/50'}`} />
                            <span className="text-[11px] text-foreground-500">Swap</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Per-pair swap cost breakdown */}
                  <div className="border-t border-background-100 pt-4">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500 mb-3">
                      Swap cost by currency pair
                    </div>
                    <div className="space-y-2.5">
                      {samplePairs.map((sp, pi) => {
                        const pc = data.pairCosts[pi];
                        const maxForPair = Math.max(Math.abs(pc.longCost), Math.abs(pc.shortCost), 1);
                        const barPct = Math.min((Math.abs(pc.longCost) / globalMaxSwap) * 100, 100);
                        const isCostly = Math.abs(pc.longCost) >= 1000 || Math.abs(pc.shortCost) >= 1000;
                        const worstCost = Math.abs(pc.longCost) >= Math.abs(pc.shortCost) ? pc.longCost : pc.shortCost;

                        return (
                          <div key={sp.pair}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[12px] font-[750] text-foreground-800">{sp.pair}</span>
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">
                                  {sp.label}
                                </span>
                              </div>
                              {!isDay && (
                                <span className={`text-[12px] font-bold tabular-nums ${isCostly ? 'text-[#EF4444]' : 'text-foreground-700'}`}>
                                  {fmt(worstCost)}
                                </span>
                              )}
                              {isDay && (
                                <span className="text-[12px] font-semibold text-foreground-400">$0</span>
                              )}
                            </div>

                            {!isDay ? (
                              <div className="w-full h-1.5 bg-background-50 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-700 ${
                                    worstCost < 0
                                      ? 'bg-[#EF4444]/40'
                                      : worstCost > 0
                                      ? 'bg-[#22C55E]/40'
                                      : 'bg-background-200'
                                  }`}
                                  style={{ width: `${Math.max(barPct, 2)}%` }}
                                />
                              </div>
                            ) : (
                              <div className="w-full h-1.5 bg-background-50 rounded-full overflow-hidden">
                                <div className="h-full w-0 bg-background-200 rounded-full" />
                              </div>
                            )}

                            {!isDay && (
                              <div className="flex items-center gap-3 mt-0.5">
                                <span className={`text-[10px] ${pc.longCost < 0 ? 'text-[#EF4444]' : pc.longCost > 0 ? 'text-[#22C55E]' : 'text-foreground-400'}`}>
                                  Long {fmtPrecise(pc.longCost)}
                                </span>
                                <span className={`text-[10px] ${pc.shortCost < 0 ? 'text-[#EF4444]' : pc.shortCost > 0 ? 'text-[#22C55E]' : 'text-foreground-400'}`}>
                                  Short {fmtPrecise(pc.shortCost)}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Scenario insight footer */}
                  <div
                    className={`mt-5 pt-4 border-t ${
                      isDay
                        ? 'border-accent-100'
                        : isSwing
                        ? 'border-secondary-100'
                        : 'border-primary-100'
                    }`}
                  >
                    <p className={`text-[12px] leading-relaxed ${
                      isDay ? 'text-accent-700' : isSwing ? 'text-secondary-700' : 'text-primary-700'
                    }`}>
                      {isDay && 'All cost is spread. Swap never enters the equation — but 100 trades × 0.5 lots × 1.5 pip spread adds up to real money. The frequency IS the cost driver here.'}
                      {isSwing && 'Swap is creeping in. At 5 days per trade, EURUSD swap is just $960 — manageable. But switch to USDTRY and you are looking at $45,600/month. The pair you pick matters more than your entry timing.'}
                      {isPosition && 'Swap is now the dominant cost — more than 90% of what you pay monthly. At this horizon, choosing a low-swap pair or a swap-positive direction is not optimization, it is survival.'}
                    </p>
                  </div>
                </div>

                {/* Bottom highlight bar for position trader */}
                {isPosition && (
                  <div className="px-6 py-3 bg-primary-50/60 border-t border-primary-100">
                    <p className="text-[12px] font-semibold text-primary-700">
                      At 30-day holds, swap is not a footnote — it is the main story. Pick your pair and direction with swap in mind.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── HEAT MAP: Full cross-comparison grid ── */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden mb-10">
          <div className="px-4 md:px-8 py-4 md:py-5 border-b border-background-100">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <div className="text-[14px] font-[750] text-foreground-950 mb-0.5">
                  Swap cost heat map
                </div>
                <div className="text-[11px] md:text-[12px] text-foreground-500">
                  All 18 combinations — pair × scenario × direction. Color intensity scales with cost magnitude.
                </div>
              </div>
              <div className="flex items-center gap-3 md:gap-4 text-[10px] md:text-[11px] flex-wrap">
                <div className="flex items-center gap-1.5 whitespace-nowrap">
                  <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-sm bg-[#EF4444]/15 border border-[#EF4444]/25 flex-shrink-0" />
                  <span className="text-foreground-500">You pay</span>
                </div>
                <div className="flex items-center gap-1.5 whitespace-nowrap">
                  <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-sm bg-[#22C55E]/12 border border-[#22C55E]/20 flex-shrink-0" />
                  <span className="text-foreground-500">You earn</span>
                </div>
                <div className="flex items-center gap-1.5 whitespace-nowrap">
                  <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-sm bg-white border border-background-200 flex-shrink-0" />
                  <span className="text-foreground-500">Swap-free</span>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto overscroll-x-contain">
            <div className="min-w-[680px]">
              {/* Column headers */}
              <div className="grid grid-cols-[130px_repeat(3,1fr)] md:grid-cols-[150px_repeat(3,1fr)] border-b border-background-50">
                <div className="p-3 md:p-4 flex items-end">
                  <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">
                    Currency pair
                  </span>
                </div>
                {swapScenarios.map((s, idx) => (
                  <div
                    key={s.label}
                    className={`p-3 md:p-4 text-center border-l border-background-50 ${
                      idx === 2 ? 'bg-primary-50/30' : ''
                    }`}
                  >
                    <div className="text-[11px] md:text-[13px] font-[750] text-foreground-800 mb-0.5 md:mb-1">{s.label}</div>
                    <div className="flex items-center justify-center gap-1.5 md:gap-2 text-[9px] md:text-[10px] text-foreground-500 flex-wrap">
                      <span className="whitespace-nowrap">{s.tradesPerMonth} trades</span>
                      <span>·</span>
                      <span className="whitespace-nowrap">{s.lotSize} lots</span>
                      <span>·</span>
                      <span className="whitespace-nowrap">{s.avgHoldDays === 0 ? '0d' : `${s.avgHoldDays}d`}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Data rows */}
              {samplePairs.map((sp, pi) => (
                <div key={sp.pair} className={`${pi % 2 === 0 ? '' : 'bg-background-50/30'}`}>
                  {/* Long row */}
                  <div className="grid grid-cols-[130px_repeat(3,1fr)] md:grid-cols-[150px_repeat(3,1fr)] border-b border-background-50/70">
                    <div className="p-3 md:p-4 flex flex-col justify-center">
                      <div className="text-[11px] md:text-[13px] font-[750] text-foreground-950">{sp.pair}</div>
                      <div className="text-[9px] md:text-[10px] text-foreground-500 uppercase tracking-wide">{sp.label}</div>
                    </div>

                    {scenarioData.map((sd, sIdx) => {
                      const pc = sd.pairCosts[pi];
                      const intensity = Math.abs(pc.longCost) / globalMaxSwap;
                      const bg =
                        pc.longCost < 0
                          ? `rgba(239,68,68,${(intensity * 0.14).toFixed(2)})`
                          : pc.longCost > 0
                          ? `rgba(34,197,94,${(intensity * 0.12).toFixed(2)})`
                          : undefined;

                      return (
                        <div
                          key={`${sp.pair}-long-${sIdx}`}
                          className={`p-3 md:p-4 text-center border-l border-background-50/70 flex flex-col justify-center ${
                            sIdx === 2 ? 'bg-primary-50/20' : ''
                          }`}
                          style={bg ? { backgroundColor: bg } : undefined}
                        >
                          <div className="text-[9px] md:text-[10px] font-bold uppercase tracking-wide text-foreground-400 mb-0.5 md:mb-1">
                            Long
                          </div>
                          <div
                            className={`text-[12px] md:text-[14px] font-bold tabular-nums ${
                              pc.longCost < 0
                                ? 'text-[#DC2626]'
                                : pc.longCost > 0
                                ? 'text-[#16A34A]'
                                : 'text-foreground-400'
                            }`}
                          >
                            {pc.longCost === 0 ? '$0' : fmtPrecise(pc.longCost)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Short row */}
                  <div className="grid grid-cols-[130px_repeat(3,1fr)] md:grid-cols-[150px_repeat(3,1fr)] border-b border-background-50/70">
                    <div className="p-3 md:p-4" />

                    {scenarioData.map((sd, sIdx) => {
                      const pc = sd.pairCosts[pi];
                      const intensity = Math.abs(pc.shortCost) / globalMaxSwap;
                      const bg =
                        pc.shortCost < 0
                          ? `rgba(239,68,68,${(intensity * 0.14).toFixed(2)})`
                          : pc.shortCost > 0
                          ? `rgba(34,197,94,${(intensity * 0.12).toFixed(2)})`
                          : undefined;

                      return (
                        <div
                          key={`${sp.pair}-short-${sIdx}`}
                          className={`p-3 md:p-4 text-center border-l border-background-50/70 flex flex-col justify-center ${
                            sIdx === 2 ? 'bg-primary-50/20' : ''
                          }`}
                          style={bg ? { backgroundColor: bg } : undefined}
                        >
                          <div className="text-[9px] md:text-[10px] font-bold uppercase tracking-wide text-foreground-400 mb-0.5 md:mb-1">
                            Short
                          </div>
                          <div
                            className={`text-[12px] md:text-[14px] font-bold tabular-nums ${
                              pc.shortCost < 0
                                ? 'text-[#DC2626]'
                                : pc.shortCost > 0
                                ? 'text-[#16A34A]'
                                : 'text-foreground-400'
                            }`}
                          >
                            {pc.shortCost === 0 ? '$0' : fmtPrecise(pc.shortCost)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile scroll hint */}
            <div className="md:hidden flex items-center justify-center gap-1.5 py-2 text-[10px] text-foreground-400 bg-background-50/50 border-t border-background-50">
              <i className="ri-arrow-left-right-line text-[12px]" />
              <span>Swipe to see all scenarios</span>
            </div>
          </div>

          <div className="px-4 md:px-8 py-3 border-t border-background-100 text-[11px] text-foreground-500 text-right">
            Color intensity normalized to {fmt(globalMaxSwap)} max · Day Trader column is swap-free by definition
          </div>
        </div>

        {/* ── Key insight ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-6">
          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[15px] font-[800] text-accent-500">!</span>
              </div>
              <div>
                <p className="text-[13px] font-semibold text-foreground-800 mb-1.5">
                  Pair selection amplifies swap by up to 50× — independently of your strategy
                </p>
                <p className="text-[12px] leading-relaxed text-foreground-500">
                  A swing trader on EURUSD pays $960/month in swap. Same parameters on USDTRY: $45,600/month.
                  The pair you trade is a bigger cost lever than your holding period, your lot size, or your broker.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[15px] font-[800] text-primary-500">!</span>
              </div>
              <div>
                <p className="text-[13px] font-semibold text-foreground-800 mb-1.5">
                  Holding period transforms swap from irrelevant to dominant
                </p>
                <p className="text-[12px] leading-relaxed text-foreground-500">
                  At 0 days (day trading): swap = $0. At 5 days: swap = 18% of costs. At 30 days: swap = 91%.
                  If you ever plan to hold past a week, swap is no longer a footnote — it is the headline.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-[12px] text-foreground-500 text-center max-w-[640px] mx-auto">
          Swap rates shown are indicative — actual values vary by broker and update daily based on interbank tom-next
          rates. Pip values: EURUSD $10, GBPJPY ¥1,000 (~$7.50), USDTRY ~$8. Annual projections assume consistent
          monthly activity. All totals use the worst direction for each pair (whichever of long/short costs more).
        </p>
      </div>
    </section>
  );
}